import React from 'react';
import { 
  Layers, 
  CheckCircle, 
  Zap, 
  ShieldAlert, 
  TrendingUp, 
  ArrowUpRight,
  AlertOctagon
} from 'lucide-react';
import { MetricsSummary } from '../types';

interface MetricCardsProps {
  metrics: MetricsSummary;
  onFilterStatus?: (status: 'All' | '200' | '403' | '409') => void;
}

export const MetricCards: React.FC<MetricCardsProps> = ({ metrics, onFilterStatus }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      
      {/* 1. Total Calls Card */}
      <div 
        onClick={() => onFilterStatus?.('All')}
        className="group relative overflow-hidden rounded-xl bg-slate-900/70 border border-slate-800 p-4 hover:border-slate-700 transition-all hover:shadow-lg cursor-pointer"
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-400 font-sans tracking-wide">
            Total Calls (Window)
          </span>
          <div className="p-2 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20 group-hover:scale-105 transition-transform">
            <Layers className="w-4 h-4" />
          </div>
        </div>
        
        <div className="mt-3 flex items-baseline justify-between">
          <div className="text-2xl font-bold font-mono text-white tracking-tight">
            {metrics.totalCalls.toLocaleString()}
          </div>
          <div className="flex items-center gap-1 text-[11px] font-mono text-emerald-400 bg-emerald-950/40 px-1.5 py-0.5 rounded border border-emerald-800/40">
            <TrendingUp className="w-3 h-3" />
            <span>+{metrics.callsDeltaPct}%</span>
          </div>
        </div>

        <div className="mt-3 text-[11px] text-slate-400 flex items-center justify-between font-mono">
          <span>Active window: last 60m</span>
          <span className="text-slate-500">Node throughput</span>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-blue-500/0 via-blue-500/40 to-blue-500/0"></div>
      </div>

      {/* 2. Success Rate % Card */}
      <div 
        onClick={() => onFilterStatus?.('200')}
        className="group relative overflow-hidden rounded-xl bg-slate-900/70 border border-slate-800 p-4 hover:border-emerald-500/30 transition-all hover:shadow-lg cursor-pointer"
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-400 font-sans tracking-wide">
            Success Rate
          </span>
          <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 group-hover:scale-105 transition-transform">
            <CheckCircle className="w-4 h-4" />
          </div>
        </div>
        
        <div className="mt-3 flex items-baseline justify-between">
          <div className="text-2xl font-bold font-mono text-emerald-400 tracking-tight">
            {metrics.successRatePct}%
          </div>
          <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-semibold">
            OPTIMAL
          </span>
        </div>

        <div className="mt-3 w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
          <div 
            className="bg-emerald-500 h-full rounded-full transition-all duration-500" 
            style={{ width: `${Math.min(100, metrics.successRatePct)}%` }}
          />
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-emerald-500/0 via-emerald-500/40 to-emerald-500/0"></div>
      </div>

      {/* 3. Average Latency Card */}
      <div className="group relative overflow-hidden rounded-xl bg-slate-900/70 border border-slate-800 p-4 hover:border-cyan-500/30 transition-all hover:shadow-lg">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-400 font-sans tracking-wide">
            Avg Execution Latency
          </span>
          <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 group-hover:scale-105 transition-transform">
            <Zap className="w-4 h-4" />
          </div>
        </div>
        
        <div className="mt-3 flex items-baseline justify-between">
          <div className="text-2xl font-bold font-mono text-cyan-300 tracking-tight">
            {metrics.avgLatencyMs}<span className="text-sm font-normal text-slate-400 ml-1">ms</span>
          </div>
          <div className="text-[11px] font-mono text-slate-400 bg-slate-800/80 px-2 py-0.5 rounded border border-slate-700">
            p95: <span className="text-cyan-300 font-semibold">{metrics.p95LatencyMs}ms</span>
          </div>
        </div>

        <div className="mt-3 text-[11px] text-slate-400 flex items-center justify-between font-mono">
          <span>Tailscale mesh RTT: ~18ms</span>
          <span className="text-cyan-400/80">Sub-150ms target</span>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-cyan-500/0 via-cyan-500/40 to-cyan-500/0"></div>
      </div>

      {/* 4. Security Blocked Attempts Card */}
      <div 
        onClick={() => onFilterStatus?.('403')}
        className="group relative overflow-hidden rounded-xl bg-slate-900/70 border border-slate-800 p-4 hover:border-rose-500/40 transition-all hover:shadow-lg cursor-pointer"
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-400 font-sans tracking-wide">
            Security Interceptions (403)
          </span>
          <div className="p-2 rounded-lg bg-rose-500/10 text-rose-400 border border-rose-500/20 group-hover:scale-105 transition-transform glow-rose">
            <ShieldAlert className="w-4 h-4" />
          </div>
        </div>
        
        <div className="mt-3 flex items-baseline justify-between">
          <div className="text-2xl font-bold font-mono text-rose-400 tracking-tight">
            {metrics.securityBlockedCount}
          </div>
          <div className="flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30 font-semibold">
            <AlertOctagon className="w-3 h-3" />
            <span>BLOCKED</span>
          </div>
        </div>

        <div className="mt-3 text-[11px] text-slate-400 flex items-center justify-between font-mono">
          <span>Guardrails: <strong className="text-slate-300">ACTIVE</strong></span>
          <span className="text-rose-400/90 font-medium">Click to inspect</span>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-rose-500/0 via-rose-500/40 to-rose-500/0"></div>
      </div>

    </div>
  );
};
